import React, { useMemo } from 'react'
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ReferenceLine, ResponsiveContainer, Dot
} from 'recharts'

interface FunctionGraphProps {
  slope: number
  intercept: number
  xRange?: [number, number]
  height?: number
  showKeyPoints?: boolean
  className?: string
  color?: string
}

const CustomDot = (props: any) => {
  const { cx, cy, payload } = props
  if (payload.isKey) {
    return <circle cx={cx} cy={cy} r={5} fill="#ef4444" stroke="white" strokeWidth={2} />
  }
  return null
}

export default function FunctionGraph({
  slope,
  intercept,
  xRange = [-5, 5],
  height = 300,
  showKeyPoints = true,
  className = '',
  color = '#2563eb',
}: FunctionGraphProps) {
  const data = useMemo(() => {
    const points = []
    const [xMin, xMax] = xRange
    const step = (xMax - xMin) / 20
    const yIntercept = intercept
    const xIntercept = slope !== 0 ? -intercept / slope : null

    for (let x = xMin; x <= xMax; x += step) {
      const xRounded = Math.round(x * 10) / 10
      const y = slope * xRounded + intercept
      const isYIntercept = showKeyPoints && Math.abs(xRounded) < 0.1
      const isXIntercept = showKeyPoints && xIntercept !== null && Math.abs(xRounded - xIntercept) < 0.1
      points.push({
        x: xRounded,
        y: Math.round(y * 100) / 100,
        isKey: isYIntercept || isXIntercept,
      })
    }
    return points
  }, [slope, intercept, xRange, showKeyPoints])

  const equation = intercept === 0
    ? `y = ${slope}x`
    : intercept > 0
      ? `y = ${slope}x + ${intercept}`
      : `y = ${slope}x ${intercept}`

  const xIntercept = slope !== 0 ? Math.round(-intercept / slope * 100) / 100 : null

  const CustomTooltipContent = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const d = payload[0].payload
      return (
        <div className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm shadow-lg">
          <p className="text-slate-600">x = <span className="font-bold text-slate-800">{d.x}</span></p>
          <p className="text-slate-600">y = <span className="font-bold text-primary-600">{d.y}</span></p>
        </div>
      )
    }
    return null
  }

  return (
    <div className={`graph-container ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <span className="font-mono text-sm font-semibold text-slate-700">{equation}</span>
        <div className="flex gap-3 text-xs text-slate-500">
          {showKeyPoints && (
            <>
              <span>기울기: <strong className={slope > 0 ? 'text-emerald-600' : 'text-red-500'}>{slope}</strong></span>
              <span>y절편: <strong className="text-blue-600">{intercept}</strong></span>
              {xIntercept !== null && (
                <span>x절편: <strong className="text-orange-600">{xIntercept}</strong></span>
              )}
            </>
          )}
        </div>
      </div>

      {/* Chart */}
      <div style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis
              dataKey="x"
              tickLine={false}
              axisLine={{ stroke: '#94a3b8' }}
              tick={{ fontSize: 11, fill: '#64748b' }}
              domain={[xRange[0], xRange[1]]}
              type="number"
            />
            <YAxis
              tickLine={false}
              axisLine={{ stroke: '#94a3b8' }}
              tick={{ fontSize: 11, fill: '#64748b' }}
              width={40}
            />
            <Tooltip content={<CustomTooltipContent />} />
            <ReferenceLine x={0} stroke="#94a3b8" strokeWidth={1.5} />
            <ReferenceLine y={0} stroke="#94a3b8" strokeWidth={1.5} />
            {/* y절편 표시 */}
            {showKeyPoints && (
              <ReferenceLine
                y={intercept}
                stroke="#3b82f6"
                strokeDasharray="4 4"
                strokeWidth={1}
                label={{ value: `y절편(${intercept})`, position: 'right', fontSize: 10, fill: '#3b82f6' }}
              />
            )}
            <Line
              type="linear"
              dataKey="y"
              stroke={color}
              strokeWidth={2.5}
              dot={<CustomDot />}
              activeDot={{ r: 6, stroke: color, strokeWidth: 2, fill: 'white' }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Legend */}
      {showKeyPoints && (
        <div className="px-4 py-2 border-t border-slate-100 flex gap-4 text-xs text-slate-500">
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-0.5 bg-primary-500 inline-block" />
            {slope > 0 ? '📈 증가 (우상향)' : slope < 0 ? '📉 감소 (우하향)' : '➡️ 수평선'}
          </span>
          {intercept === 0 && (
            <span className="text-emerald-600 font-medium">✓ 정비례 함수 (원점 통과)</span>
          )}
        </div>
      )}
    </div>
  )
}
